// src/pages/FriendCirclesLanding.jsx
//
// What Friend Circles are, for someone who isn't signed in. Every sentence
// here has to be true of the app as built (docs/CIRCLES_PULSE.md): a circle
// holds no writing, answers are named once they reveal, and nobody runs it.
import { motion } from 'framer-motion';
import { Users, Sparkles, MessageCircle, HelpCircle, BookOpen, ArrowRight, Lock, Zap, DoorOpen } from 'lucide-react';
import { usePrivacyAuth } from '../context/PrivacyAuthContext';
import { useApp } from '../context/AppContext';
import { getPulseQuestion } from '../data/circlePulseQuestions';
import { CIRCLE_TALK_PROMPTS } from '../data/circleTalkPrompts';

// Read inside the component, never at module scope. The production build puts
// this page and the data it reads in different chunks that import each other
// (the "Circular chunk" warnings), and a module-scope read ran before the data
// chunk had initialised: "Cannot access '…' before initialization", and the
// whole app rendered a blank page on load.
const samplePulse = () => getPulseQuestion('pulse-recharge');
const sampleTalk = () => CIRCLE_TALK_PROMPTS[6][0];

export default function FriendCirclesLanding() {
  const { user } = usePrivacyAuth();
  const { setCurrentView } = useApp();
  const SAMPLE_PULSE = samplePulse();
  const SAMPLE_TALK = sampleTalk();

  const handleGetStarted = () => {
    if (user) {
      // User is authenticated, go to welcome page first
      setCurrentView('friend-circles-welcome');
    } else {
      // Redirect to signup with return URL to welcome page
      window.location.href = '/signup.html?redirect=friend-circles-welcome';
    }
  };

  const features = [
    {
      icon: BookOpen,
      title: 'The same module',
      description: 'The circle picks one of the eight modules. You can see whether the others have finished it, and nothing more: not how far, not how fast, and nothing anyone wrote.',
      color: 'from-purple-500 to-pink-500',
    },
    {
      icon: HelpCircle,
      title: 'One question, all at once',
      description: 'Everyone picks an answer from a few fixed options. Nobody sees anyone else’s until everyone has, then they all show together, with names.',
      color: 'from-orange-500 to-yellow-500',
    },
    {
      icon: MessageCircle,
      title: 'Then talk, in person',
      description: 'A few questions to take away and talk about when you’re together. There’s nowhere to type an answer, on purpose.',
      color: 'from-blue-500 to-cyan-500',
    },
    {
      icon: Lock,
      title: 'Nothing to write, nothing to pile on',
      description: 'No posts, comments, reactions or chat. The only words in a circle are its name and the names you go by.',
      color: 'from-green-500 to-emerald-500',
    },
    {
      icon: DoorOpen,
      title: 'Nobody in charge',
      description: 'Nobody approves anyone or removes anyone. Anyone can leave whenever they like, including whoever started it, and nobody is told.',
      color: 'from-red-500 to-orange-500',
    },
  ];

  const steps = [
    {
      number: '1',
      title: 'Start a circle',
      description: 'Give it a name, pick an emoji, and you get a six-character code.',
    },
    {
      number: '2',
      title: 'Send the code privately',
      description: 'To friends you know, in a direct message or your own group chat, never anywhere public. Anyone with the code can join, and anyone in the circle can swap it for a new one if it gets out.',
    },
    {
      number: '3',
      title: 'Read, answer, talk',
      description: 'Same module, same question, answers revealed together. The conversation happens when you see each other.',
    },
  ];

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-purple-900 to-slate-900 pt-20 sm:pt-32">
      <div className="container mx-auto px-4 sm:px-6 py-12">
        {/* Hero Section */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="text-center max-w-4xl mx-auto mb-16"
        >
          <motion.div
            initial={{ scale: 0 }}
            animate={{ scale: 1 }}
            transition={{ delay: 0.2, type: 'spring', stiffness: 200 }}
            className="inline-block mb-6"
          >
            <div className="relative">
              <div className="absolute inset-0 bg-gradient-to-r from-purple-500/20 to-pink-500/20 blur-3xl rounded-full" />
              <div className="relative bg-gradient-to-br from-purple-500 to-pink-500 p-6 rounded-3xl shadow-2xl">
                <Users className="w-16 h-16 text-white" />
              </div>
            </div>
          </motion.div>

          <motion.h1
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.3 }}
            className="text-4xl sm:text-5xl md:text-6xl font-bold text-white mb-6"
          >
            Friend Circles
            <span className="block mt-2 bg-gradient-to-r from-purple-400 to-pink-400 bg-clip-text text-transparent">
              The same module, then talk
            </span>
          </motion.h1>

          <motion.p
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.4 }}
            className="text-xl text-purple-200 mb-8 max-w-2xl mx-auto"
          >
            Do the modules with friends you already know. Read the same thing, answer the same question, and talk about it when you&apos;re together. For people aged 13 and over.
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.5 }}
            className="flex flex-col gap-4 justify-center items-center"
          >
            <button
              onClick={handleGetStarted}
              className="group relative px-8 py-4 bg-gradient-to-r from-purple-500 to-pink-500 hover:from-purple-600 hover:to-pink-600 text-white font-bold rounded-xl transition-all shadow-lg hover:shadow-2xl hover:scale-105"
            >
              <span className="flex items-center gap-2">
                {user ? (
                  <>
                    <Sparkles className="w-5 h-5" />
                    Create Your Circle
                    <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
                  </>
                ) : (
                  <>
                    <Lock className="w-5 h-5" />
                    Sign Up to Get Started
                    <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
                  </>
                )}
              </span>
            </button>

            {!user && (
              <div className="flex flex-col sm:flex-row items-center gap-2 text-sm text-purple-300">
                <span>Already have an account?</span>
                <a
                  href="/login.html?redirect=friend-circles-welcome"
                  className="text-purple-400 hover:text-purple-300 underline font-medium transition-colors"
                >
                  Login here
                </a>
              </div>
            )}
          </motion.div>
        </motion.div>

        {/* Why It Works Section */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.6 }}
          className="max-w-3xl mx-auto mb-16"
        >
          <div className="bg-gradient-to-br from-blue-500/10 to-purple-500/10 backdrop-blur-sm rounded-2xl p-8 border border-purple-500/20">
            <h2 className="text-2xl font-bold text-white mb-4 flex items-center gap-2">
              <Zap className="w-6 h-6 text-yellow-400" />
              Why do it together
            </h2>
            <div className="space-y-3 text-purple-200">
              <p>
                Most of what the modules cover happens between people, not inside one head. Reading the same
                module as your friends gives you something to talk about that isn&apos;t about anyone in particular.
              </p>
              <p>
                The question you all answer is about yourselves: how you recharge, what you do when something&apos;s
                wrong, what makes an apology land. When the answers show, the interesting part is where you
                differ, and that&apos;s a conversation for the walk home, not a comment thread.
              </p>
            </div>
          </div>
        </motion.div>

        {/* Features Grid */}
        <div className="max-w-6xl mx-auto mb-16">
          <h2 className="text-2xl sm:text-3xl font-bold text-white text-center mb-12">
            What a circle is
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {features.map((feature, index) => (
              <motion.div
                key={feature.title}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.7 + index * 0.1 }}
                className="bg-white/5 backdrop-blur-sm rounded-xl p-6 border border-white/10 hover:border-purple-500/30 transition-all hover:scale-105"
              >
                <div className={`inline-flex p-3 bg-gradient-to-r ${feature.color} rounded-lg mb-4`}>
                  <feature.icon className="w-6 h-6 text-white" />
                </div>
                <h3 className="text-xl font-bold text-white mb-2">{feature.title}</h3>
                <p className="text-purple-200">{feature.description}</p>
              </motion.div>
            ))}
          </div>
        </div>

        {/* How It Works */}
        <div className="max-w-4xl mx-auto mb-16">
          <h2 className="text-2xl sm:text-3xl font-bold text-white text-center mb-12">
            How It Works
          </h2>
          <div className="space-y-6">
            {steps.map((step, index) => (
              <motion.div
                key={step.number}
                initial={{ opacity: 0, x: -20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: 1 + index * 0.15 }}
                className="flex gap-6 items-start"
              >
                <div className="flex-shrink-0">
                  <div className="w-12 h-12 bg-gradient-to-br from-purple-500 to-pink-500 rounded-full flex items-center justify-center text-white font-bold text-xl shadow-lg">
                    {step.number}
                  </div>
                </div>
                <div className="flex-1 bg-white/5 backdrop-blur-sm rounded-xl p-6 border border-white/10">
                  <h3 className="text-xl font-bold text-white mb-2">{step.title}</h3>
                  <p className="text-purple-200">{step.description}</p>
                </div>
              </motion.div>
            ))}
          </div>
        </div>

        {/* Sample Reflection Prompts */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 1.5 }}
          className="max-w-4xl mx-auto mb-16"
        >
          <div className="bg-gradient-to-br from-purple-500/10 to-pink-500/10 backdrop-blur-sm rounded-2xl p-8 border border-purple-500/20">
            <h2 className="text-2xl font-bold text-white mb-6 text-center">
              What it looks like
            </h2>
            <div className="space-y-4">
              <div className="bg-white/5 rounded-lg p-4 border-l-4 border-orange-500">
                <p className="text-sm text-orange-300 mb-2">A circle question: pick one</p>
                <p className="text-white font-medium mb-3">{SAMPLE_PULSE.question}</p>
                <ul className="space-y-1.5">
                  {SAMPLE_PULSE.options.map((option) => (
                    <li key={option} className="text-sm text-purple-200 bg-white/5 rounded px-3 py-2">{option}</li>
                  ))}
                </ul>
                <p className="text-xs text-purple-300 mt-3">
                  Hidden until everyone has answered, then everyone in the circle sees who picked what.
                </p>
              </div>
              <div className="bg-white/5 rounded-lg p-4 border-l-4 border-blue-500">
                <p className="text-sm text-blue-300 mb-2">Talk about this (no answer box)</p>
                <p className="text-purple-100">{SAMPLE_TALK.prompt}</p>
              </div>
            </div>
          </div>
        </motion.div>

        {/* Privacy Notice */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 1.7 }}
          className="max-w-2xl mx-auto mb-12"
        >
          <div className="bg-blue-500/10 backdrop-blur-sm rounded-xl p-6 border border-blue-500/30">
            <div className="flex gap-3">
              <Lock className="w-5 h-5 text-blue-400 flex-shrink-0 mt-1" />
              <div className="space-y-2 text-sm text-blue-200">
                <p className="font-semibold text-blue-100">What a circle keeps, and who sees it</p>
                <p>
                  Its name and emoji, its code, who&apos;s in it and the names they go by, whether each person has
                  finished the module it&apos;s on, and which option each person picked for each question. Only
                  people in the circle can see any of it.
                </p>
                <p>
                  Answers are not anonymous: once a question reveals, everyone it was asked sees who picked what.
                  Someone who joins later doesn&apos;t see questions asked before they arrived. Leaving takes your
                  name and your answers with you.
                </p>
              </div>
            </div>
          </div>
        </motion.div>

        {/* CTA Section */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 1.9 }}
          className="text-center"
        >
          <div className="max-w-2xl mx-auto bg-gradient-to-br from-purple-500/20 to-pink-500/20 backdrop-blur-sm rounded-2xl p-12 border border-purple-500/30">
            <h2 className="text-2xl sm:text-3xl font-bold text-white mb-4">
              Ready to start one?
            </h2>
            <p className="text-purple-200 mb-8">
              {user ? (
                "It takes a minute to start one"
              ) : (
                "Sign up for free and create your first Friend Circle"
              )}
            </p>
            <button
              onClick={handleGetStarted}
              className="group px-8 sm:px-12 py-4 sm:py-5 bg-gradient-to-r from-purple-500 to-pink-500 hover:from-purple-600 hover:to-pink-600 text-white text-lg sm:text-xl font-bold rounded-xl transition-all shadow-2xl hover:shadow-purple-500/50 hover:scale-105"
            >
              <span className="flex items-center gap-3">
                {user ? (
                  <>
                    <Users className="w-6 h-6" />
                    Create Your Circle Now
                    <ArrowRight className="w-6 h-6 group-hover:translate-x-1 transition-transform" />
                  </>
                ) : (
                  <>
                    <Sparkles className="w-6 h-6" />
                    Get Started Free
                    <ArrowRight className="w-6 h-6 group-hover:translate-x-1 transition-transform" />
                  </>
                )}
              </span>
            </button>

            {!user && (
              <p className="mt-4 text-sm text-purple-300">
                Already have an account?{' '}
                <a
                  href="/login.html?redirect=friend-circles-welcome"
                  className="text-purple-400 hover:text-purple-300 underline font-medium transition-colors"
                >
                  Login here
                </a>
              </p>
            )}
          </div>
        </motion.div>
      </div>
    </div>
  );
}
